import { toDate } from 'date-fns';
import { parse, ParsedQs } from 'qs';

import { getCaterer } from 'lib/algolia/client';
import { Caterer } from 'lib/algolia/model';
import { Answers } from 'qualification/context/model';
import { QualificationInitializeOptions } from 'qualification/context/reducer/initialize';
import { QuestionId } from 'qualification/schema/model';
import { isValidCateringCategory } from 'qualification/schema/questions/cateringCategoriesQuestion';
import { isValidCity } from 'qualification/schema/questions/cityQuestion';
import { StepId, steps } from 'qualification/schema/steps';
import { getCityInLocalStorage } from 'shared/helpers/cities';

type ParsedQueryParameter =
  | string
  | ParsedQs
  | string[]
  | ParsedQs[]
  | undefined;

type HandledQuestionIds = Extract<
  QuestionId,
  'city' | 'event_date' | 'catering_categories'
>;

type HandledQueryParameters =
  | HandledQuestionIds
  | 'selectedCatererId'
  | 'leadSource';

const HANDLED_QUESTION_IDS: HandledQuestionIds[] = [
  'catering_categories',
  'city',
  'event_date',
];
const HANDLED_QUERY_PARAMS: HandledQueryParameters[] = [
  ...HANDLED_QUESTION_IDS,
  'selectedCatererId',
];

export async function getInitialFunnelDataFromQueryStrings(): Promise<
  QualificationInitializeOptions
> {
  const params = parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  const {
    city,
    catering_categories,
    event_date,
    selectedCatererId,
    leadSource,
  } = params as Record<HandledQueryParameters, ParsedQueryParameter>;

  const answers: Answers = {};

  const cityFromLocalStorage = getCityInLocalStorage();
  const nonSkippableQuestions: StepId[] = [];
  if (!city && cityFromLocalStorage && isValidCity(cityFromLocalStorage)) {
    answers.city = cityFromLocalStorage;
  }

  if (city && isValidCity(city)) {
    answers.city = city;
  }

  if (
    (city && !isValidCity(city)) ||
    (cityFromLocalStorage && !isValidCity(cityFromLocalStorage))
  ) {
    answers.city = 'other';
  }

  if (Array.isArray(catering_categories)) {
    const validCateringCategories = (catering_categories as string[]).filter(
      isValidCateringCategory
    );

    if (validCateringCategories.length) {
      answers.catering_categories = validCateringCategories;
    }
  }

  let selectedCaterer: Caterer | undefined = undefined;

  if (typeof selectedCatererId === 'string') {
    selectedCaterer = await getCaterer(selectedCatererId);
  }

  if (event_date && typeof event_date === 'string') {
    const timestamp = parseInt(event_date, 10);
    answers.event_date = toDate(timestamp);
    answers.has_recurring_need = false;
    nonSkippableQuestions.push(steps.eventDateAndLeadType.id);
  }

  return {
    answers,
    nonSkippableQuestions,
    selectedCaterer,
    leadSource: typeof leadSource === 'string' ? leadSource : undefined,
  };
}

export function getQueryParamsWithoutAnswers() {
  const queryParams = parse(window.location.search, {
    ignoreQueryPrefix: true,
  });

  if (typeof queryParams === 'object') {
    HANDLED_QUERY_PARAMS.forEach((answerKey) => {
      delete queryParams[answerKey];
    });
    delete queryParams.leadSource;
    delete queryParams.selectedCaterer;
  }

  return queryParams as { [key: string]: string };
}
